/**
 * Slides the header's top offset to 0 once the marquee above it scrolls out
 * of view, and fades in a blur and a tint behind it as the page scrolls
 * further.
 *
 * The header is always position: fixed (see sections/header.liquid); this
 * only toggles a data-pinned attribute that the header's own CSS uses to
 * move between top: var(--marquee-height) and top: 0. Toggling the position
 * property itself, rather than just its offset, was tried first and caused
 * the page's total height to change at the exact moment of the toggle,
 * shifting everything below the header by its height mid-scroll. Sliding an
 * offset on a permanently fixed element changes nothing else's layout.
 *
 * The threshold is read from --marquee-height rather than hardcoded, so it
 * cannot drift out of sync with the value that actually sizes the marquee.
 * Pinning only once the marquee has fully scrolled away is what lets the
 * header sit flush at the top without ever covering something still visible.
 */
(() => {
  const header = document.querySelector(".site-header");
  if (!header) return;

  const DISTANCE = 140; // px of scroll over which the blur/tint effect fully ramps in
  const MAX_BLUR = 14; // px
  const MAX_TINT = 82; // background tint strength, percent

  /**
   * Where the header switches to dark ink and the dark logo.
   *
   * Not at data-pinned. Pinning happens as soon as the marquee clears, 36px in,
   * where the tint is still only about a fifth faded in -- dark text there would
   * sit on a nearly transparent bar over the hero photograph. This waits until
   * the tint is mostly present and the backdrop blur has flattened whatever is
   * behind it, so the bar is light enough to carry dark text.
   */
  const SOLID_AT = 0.75;

  const marqueeHeight =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--marquee-height")) || 0;

  let ticking = false;

  const update = () => {
    ticking = false;
    const y = window.scrollY;

    header.toggleAttribute("data-pinned", y >= marqueeHeight);

    const progress = Math.min(1, Math.max(0, y / DISTANCE));
    header.toggleAttribute("data-solid", progress >= SOLID_AT);
    header.style.setProperty("--nav-blur", `${(progress * MAX_BLUR).toFixed(2)}px`);
    header.style.setProperty(
      "--nav-tint",
      `color-mix(in oklab, var(--color-background) ${(progress * MAX_TINT).toFixed(1)}%, transparent)`,
    );
  };

  const request = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  addEventListener("scroll", request, { passive: true });
  addEventListener("resize", request, { passive: true });
  update();
})();
